import { useState, useEffect } from 'react';
import { FileText, FolderKanban, UserCheck, Users, Clock, TrendingUp, AlertCircle, Shield, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useTheme } from '@/context/ThemeContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://127.0.0.1:8000';
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  // TODO: Get user role from backend/auth context
  const [userRole, setUserRole] = useState('Admin'); // 'Admin' or 'Core Head' or 'Owner'
  const { isDark } = useTheme();
  const [stats, setStats] = useState({
    activeProjects: 0,
    totalIdeas: 0,
    pendingApplications: 0,
    totalUsers: 0,
    pendingProjects: 0,
  });
  const [loading, setLoading] = useState(true);
  
  // Determine if user is Core Head or higher
  const isCoreHead = userRole === 'Core Head' || userRole === 'Admin' || userRole === 'Owner';

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [projectsRes, ideasRes, teamRequestsRes, usersRes] = await Promise.all([
        axios.get(`${API}/projects`),
        axios.get(`${API}/ideas`),
        axios.get(`${API}/team-requests`),
        axios.get(`${API}/users`).catch(() => ({ data: [] })), // Users endpoint might not exist yet
      ]);

      const projects = projectsRes.data || [];
      const ideas = ideasRes.data || [];
      const teamRequests = teamRequestsRes.data || [];
      const users = usersRes.data || [];

      // Count pending applications (team requests without status or with pending status)
      const pendingApplications = teamRequests.filter(req => !req.status || req.status === 'pending').length;
      
      // Count pending projects (projects that might need approval - you can customize this logic)
      const pendingProjects = projects.filter(p => !p.is_featured || p.status === 'Ideation').length;

      setStats({
        activeProjects: projects.length,
        totalIdeas: ideas.length,
        pendingApplications,
        totalUsers: users.length,
        pendingProjects,
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const statsCards = [
    { label: 'Active Projects', value: stats.activeProjects, icon: FolderKanban, color: 'bg-blue-500', link: '/admin/projects' },
    { label: 'Submitted Ideas', value: stats.totalIdeas, icon: FileText, color: 'bg-purple-500', link: '/admin/problem-statements' },
    { label: 'Pending Applications', value: stats.pendingApplications, icon: UserCheck, color: 'bg-orange-500', link: '/admin/applications' },
    { label: 'Total Users', value: stats.totalUsers, icon: Users, color: 'bg-green-500', link: '/admin/users' },
    { label: 'Awaiting Approval', value: stats.pendingProjects, icon: CheckCircle, color: 'bg-gradient-to-r from-purple-500 to-pink-500', link: '/admin/review-approvals' },
    { label: 'This Week Views', value: '1.2K', icon: TrendingUp, color: 'bg-pink-500', link: '/admin/analytics' },
  ];

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Role Badge */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0">
        <div className="flex items-center gap-3">
          {isCoreHead && (
            <div className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg transition-colors ${
              isDark 
                ? 'bg-purple-600/30 text-purple-200 border border-purple-500/50' 
                : 'bg-purple-100 text-purple-700 border border-purple-200'
            }`}>
              <Shield className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="text-sm sm:text-base font-semibold">{userRole} Dashboard</span>
            </div>
          )}
        </div>
        <Link to="/admin/review-approvals" className="w-full sm:w-auto">
          <Button className="w-full sm:w-auto bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white text-sm sm:text-base">
            <CheckCircle className="w-4 h-4 mr-2" />
            Review & Approvals ({stats.pendingProjects})
          </Button>
        </Link>
      </div>

      {/* Stats Grid */}
      {loading ? (
        <div className="text-center py-12">
          <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>Loading dashboard data...</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {statsCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Link key={stat.label} to={stat.link || '#'}>
              <div className={`rounded-xl p-6 shadow-md border cursor-pointer ${
                isDark 
                  ? 'bg-gradient-to-br from-[#2d1b4e] to-[#1a0b2e] border-purple-500/20 hover:border-purple-500/40' 
                  : 'bg-white border-gray-200 hover:bg-gray-50 shadow-sm'
              }`}>
                <div className="flex flex-col gap-4">
                  <div className="flex items-center justify-between">
                    <p className={`text-sm font-medium ${
                      isDark ? 'text-gray-400' : 'text-gray-600'
                    }`}>{stat.label}</p>
                    <div className={`${stat.color} p-2.5 rounded-lg flex items-center justify-center`}>
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                  </div>
                  <p className={`text-3xl font-bold ${
                    isDark ? 'text-white' : 'text-gray-900'
                  }`}>{stat.value}</p>
                </div>
              </div>
            </Link>
          );
        })}
        </div>
      )}

      {/* Quick Actions & Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Quick Actions */}
        <div className={`rounded-xl p-6 shadow-md border transition-colors ${
          isDark 
            ? 'bg-gradient-to-br from-[#2d1b4e] to-[#1a0b2e] border-purple-500/20' 
            : 'bg-white border-gray-200 shadow-sm'
        }`}>
          <h2 className={`text-xl font-semibold mb-4 transition-colors ${
            isDark ? 'text-white' : 'text-gray-900'
          }`}>Quick Actions</h2>
          <div className="flex flex-col gap-5">
            <Link to="/admin/problem-statements">
              <Button className="w-full justify-start bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white hover:text-white">
                <FileText className="w-4 h-4 mr-2" />
                Publish New Problem Statement
              </Button>
            </Link>
            <Link to="/admin/projects">
              <Button variant="outline" className={`w-full justify-start ${
                isDark 
                  ? 'border-purple-500/30 text-purple-200 hover:bg-purple-500/10 hover:text-white' 
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50 hover:text-gray-700'
              }`}>
                <FolderKanban className="w-4 h-4 mr-2" />
                Review Pending Projects
              </Button>
            </Link>
            <Link to="/admin/applications">
              <Button variant="outline" className={`w-full justify-start ${
                isDark 
                  ? 'border-purple-500/30 text-purple-200 hover:bg-purple-500/10 hover:text-white' 
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50 hover:text-gray-700'
              }`}>
                <UserCheck className="w-4 h-4 mr-2" />
                Review Applications
              </Button>
            </Link>
            {isCoreHead && (
              <>
                <Link to="/admin/review-approvals">
                  <Button variant="outline" className={`w-full justify-start ${
                    isDark 
                      ? 'border-purple-500/30 text-purple-200 hover:bg-purple-500/10 hover:text-white' 
                      : 'border-purple-300 text-purple-700 hover:bg-purple-50 hover:text-purple-700'
                  }`}>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Review & Approvals
                  </Button>
                </Link>
                <Link to="/admin/scheduling">
                  <Button variant="outline" className={`w-full justify-start ${
                    isDark 
                      ? 'border-purple-500/30 text-purple-200 hover:bg-purple-500/10 hover:text-white' 
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50 hover:text-gray-700'
                  }`}>
                    <Clock className="w-4 h-4 mr-2" />
                    Schedule Promotion Week
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>

        {/* Recent Activity */}
        <div className={`rounded-xl p-6 shadow-md border transition-colors ${
          isDark 
            ? 'bg-gradient-to-br from-[#2d1b4e] to-[#1a0b2e] border-purple-500/20' 
            : 'bg-white border-gray-200 shadow-sm'
        }`}>
          <h2 className={`text-xl font-semibold mb-4 transition-colors ${
            isDark ? 'text-white' : 'text-gray-900'
          }`}>Recent Activity</h2>
          <div className="space-y-3">
            <div className={`flex items-center gap-3 p-3 rounded-lg ${
              isDark ? 'hover:bg-purple-500/10' : 'hover:bg-gray-50'
            }`}>
              <div className="w-2 h-2 bg-green-500 rounded-full flex-shrink-0"></div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm ${
                  isDark ? 'text-gray-200' : 'text-gray-900'
                }`}>New project submitted: "EcoTrack"</p>
                <p className={`text-xs mt-1 ${
                  isDark ? 'text-gray-400' : 'text-gray-500'
                }`}>2 hours ago</p>
              </div>
            </div>
            <div className={`flex items-center gap-3 p-3 rounded-lg ${
              isDark ? 'hover:bg-purple-500/10' : 'hover:bg-gray-50'
            }`}>
              <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0"></div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm ${
                  isDark ? 'text-gray-200' : 'text-gray-900'
                }`}>Problem Statement published</p>
                <p className={`text-xs mt-1 ${
                  isDark ? 'text-gray-400' : 'text-gray-500'
                }`}>5 hours ago</p>
              </div>
            </div>
            <div className={`flex items-center gap-3 p-3 rounded-lg ${
              isDark ? 'hover:bg-purple-500/10' : 'hover:bg-gray-50'
            }`}>
              <div className="w-2 h-2 bg-orange-500 rounded-full flex-shrink-0"></div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm ${
                  isDark ? 'text-gray-200' : 'text-gray-900'
                }`}>3 new applications received</p>
                <p className={`text-xs mt-1 ${
                  isDark ? 'text-gray-400' : 'text-gray-500'
                }`}>1 day ago</p>
              </div>
            </div>
            <div className={`flex items-center gap-3 p-3 rounded-lg ${
              isDark ? 'hover:bg-purple-500/10' : 'hover:bg-gray-50'
            }`}>
              <div className="w-2 h-2 bg-purple-500 rounded-full flex-shrink-0"></div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm ${
                  isDark ? 'text-gray-200' : 'text-gray-900'
                }`}>Project leader assigned</p>
                <p className={`text-xs mt-1 ${
                  isDark ? 'text-gray-400' : 'text-gray-500'
                }`}>2 days ago</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;

